import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Send, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtTime } from "@/lib/langs";
import { ActivityChart } from "@/components/admin/Charts";

const PasswordDialog = ({ user, open, setOpen }) => {
  const [pwd, setPwd] = useState("");
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/users/${user.user_id}/password`, { password: pwd });
      toast.success(`Password updated for ${user.name}`);
      setPwd("");
      setOpen(false);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-none">
        <DialogHeader><DialogTitle className="font-display">Set new password · {user.name}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <input type="text" minLength={6} required className="field font-mono" placeholder="Min 6 characters" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="admin-new-password-input" />
          <button className="btn-danger w-full" data-testid="admin-save-password-button">Override password</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const SupportChat = ({ userId }) => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);
  useEffect(() => {
    const load = () => api.get(`/admin/support/${userId}`).then((r) => setMsgs(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [userId]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [msgs.length]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/admin/support/${userId}`, { text });
      setMsgs((m) => [...m, data]);
      setText("");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="flex h-[320px] flex-col border border-border" data-testid="admin-support-chat">
      <div className="border-b border-border px-4 py-2 font-mono text-[10px] tracking-widest text-muted-foreground">1:1 CHANNEL · DELIVERED AS “SYSTEM / SPEECHFLOW SUPPORT”</div>
      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {msgs.length === 0 && <p className="caret font-mono text-xs text-muted-foreground">&gt; No transmissions yet</p>}
        {msgs.map((m) => (
          <div key={m.message_id} className={`max-w-[85%] ${m.sender === "admin" ? "ml-auto text-right" : ""}`} data-testid="admin-support-message">
            <div className="mono-label !text-[9px]">{m.sender === "admin" ? "SYSTEM" : "USER"} · {fmtTime(m.created_at)}</div>
            <div className={`mt-1 inline-block px-3 py-2 text-sm ${m.sender === "admin" ? "bg-cyan-400/10 font-mono text-[13px]" : "bg-muted"}`}>{m.text}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 border-t border-border p-2">
        <input className="field !h-10" placeholder="Transmit support message…" value={text} onChange={(e) => setText(e.target.value)} data-testid="admin-support-input" />
        <button className="icon-btn !h-10 !w-10 shrink-0" data-testid="admin-support-send" aria-label="Send"><Send size={15} /></button>
      </form>
    </div>
  );
};

export const UserPanel = ({ user, onClose }) => {
  const [tel, setTel] = useState(null);
  const [pwdOpen, setPwdOpen] = useState(false);
  useEffect(() => {
    setTel(null);
    api.get(`/admin/users/${user.user_id}/telemetry`).then((r) => setTel(r.data)).catch(() => {});
  }, [user.user_id]);

  return (
    <div className="holo-panel corners fade-up p-5" data-testid="admin-user-panel">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="eyebrow">Subject // {user.user_id}</div>
          <h3 className="mt-2 truncate font-display text-xl font-extrabold">{user.name}</h3>
          <div className="truncate font-mono text-xs text-muted-foreground">{user.email}</div>
        </div>
        <button onClick={onClose} className="icon-btn shrink-0" data-testid="admin-close-user-panel" aria-label="Close"><X size={15} /></button>
      </div>
      <div className="mt-5 grid grid-cols-4 border-l border-t border-border font-mono">
        {[["MIN", tel?.user.total_minutes], ["SESS", tel?.session_count], ["ROOMS", tel?.rooms], ["MSGS", tel?.messages]].map(([l, v]) => (
          <div key={l} className="border-b border-r border-border p-3"><div className="text-lg tabular-nums">{v ?? "—"}</div><div className="mono-label !text-[9px]">{l}</div></div>
        ))}
      </div>
      <div className="mt-5"><div className="mono-label mb-2">Activity · 14d (minutes)</div><ActivityChart data={tel?.activity || []} height={140} testId="admin-user-activity-chart" /></div>
      <div className="mt-4 max-h-36 overflow-y-auto border border-border" data-testid="admin-user-sessions">
        {(tel?.sessions || []).map((s) => (
          <div key={s.session_id} className="flex justify-between border-b border-border px-3 py-1.5 font-mono text-[11px] last:border-0">
            <span>{fmtDate(s.started_at)} · {s.room_id}</span><span className="text-[color:var(--au-green)]">{s.ended_at ? `${s.minutes}m` : "LIVE"}</span>
          </div>
        ))}
        {tel && tel.sessions.length === 0 && <div className="px-3 py-3 font-mono text-[11px] text-muted-foreground">No session history</div>}
      </div>
      <button onClick={() => setPwdOpen(true)} className="btn-ghost mt-4 w-full" data-testid="admin-change-password-button"><KeyRound size={15} /> Change password</button>
      <div className="mt-4"><SupportChat userId={user.user_id} /></div>
      <PasswordDialog user={user} open={pwdOpen} setOpen={setPwdOpen} />
    </div>
  );
};
